import {
  FC, useEffect, useRef, useState,
} from 'react'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import * as Sentry from '@sentry/nextjs'

import FreeTrialModal from '@/UI/Modals/FreeTrialModal'
import FreeTrialTermsPrimerModal from '@/UI/Modals/FreeTrialTermsPrimerModal'
import useCreateWorkflow from '@/hooks/useCreateWorkflow'
import useFreeTrialLink from '@/hooks/useFreeTrialLink'

import {
  acceptFreeTrialTermsRequest,
  enrollInFreeTrialRequest,
  isEnrolled,
} from '@/api/free-trial'
import { getUserFromIdRequest } from '@/api/get-user-from-id-request'

import { destroyFreeTrialId } from '@/utils/freeTrialUtil'
import { getUserProfile, storeUserProfile } from '@/utils/localStorageUtil'
import { localizeRoute } from '@/utils/viewAsUtil'
import gridAnalytics from '@/lib/analytics'

/**
 * Split out of the main bundle: it pulls in pdf.js to rasterise the agreement
 * and DocuSeal's form to sign it, the heaviest things on this route and needed
 * by only the handful of visitors who arrive on a free trial deep link.
 *
 * `ssr: false` because pdf.js reaches for `window` and a web worker on import,
 * and this modal is only ever reached from a client-side interaction anyway.
 */
const FreeTrialTermsModal = dynamic(
  () => import('@/UI/Modals/FreeTrialTermsModal'),
  { ssr: false },
)

// The deep link only ever hands off to an authenticated page, so enrolment must
// not fire while the visitor is still working through login / create-account.
const PUBLIC_ROUTES = [
  '/login',
  '/create-account',
  '/almost-there',
  '/reset-password',
  '/invitation',
  '/logout',
  '/free-trial',
  '/auth/callback',
]

/**
 * `idle` covers both "nothing to do" and "we gave up quietly", which are the
 * same thing on screen.
 */
type Stage = 'idle' | 'primer' | 'terms' | 'welcome'

/**
 * Consumes the `/free-trial/:uuid` deep link once the user reaches the app:
 * walks them through the terms and conditions, enrols them into the campaign,
 * then welcomes them with the free trial modal.
 *
 * Terms come first because the backend refuses enrolment outright until
 * `acceptedFreeTrialTerms` is set on the customer, so nothing is spent — not
 * the campaign id, not an enrolment attempt — until the customer has agreed.
 */
export const FreeTrialEnrollment: FC = () => {
  const router = useRouter()
  const campaignId = useFreeTrialLink()

  const [stage, setStage] = useState<Stage>('idle')
  const [verifications, setVerifications] = useState<number | null | undefined>(undefined)
  const [trialDays, setTrialDays] = useState<number | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasAcceptFailed, setHasAcceptFailed] = useState(false)

  // Captured when the flow opens so the handlers do not have to re-read the
  // profile, which the enrolment step rewrites part way through.
  const identity = useRef<{ userId: string, customerId: string } | null>(null)
  const hasRun = useRef(false)

  // Profile readers (side menu, banner) only re-read on render, so leaving the welcome
  // modal does a full page load to show the newly granted modules everywhere at once.
  const { createWorkflow, isCreating } = useCreateWorkflow({
    navigate: route => window.location.assign(localizeRoute(route, router.locale)),
  })

  const isPublicRoute = PUBLIC_ROUTES.some(route => router.asPath.startsWith(route))

  useEffect(() => {
    const profile = getUserProfile()
    const customerId = profile?.customerId ?? profile?.customer?.id

    if (!campaignId || isPublicRoute || !customerId || hasRun.current) {
      return
    }

    hasRun.current = true
    identity.current = { userId: profile.userId, customerId }

    setStage('primer')
  }, [campaignId, isPublicRoute])

  // A visitor who was already logged in when they opened the link never goes
  // through the `identify` that carries `freeTrialId`, so the events carry it too.
  const continueFromPrimer = () => {
    gridAnalytics.track('free_trial_primer_continue_clicked', { freeTrialId: campaignId })
    setStage('terms')
  }

  const acceptTermsAndEnroll = async (submitterId?: number) => {
    if (!campaignId || !identity.current) {
      return
    }

    gridAnalytics.track('free_trial_terms_continue_clicked', { freeTrialId: campaignId })

    const { userId, customerId } = identity.current
    setIsSubmitting(true)
    setHasAcceptFailed(false)

    try {
      await acceptFreeTrialTermsRequest(customerId, campaignId, submitterId)
    } catch (e) {
      // Nothing was recorded and nothing was enrolled, so the deep link is still
      // good. Spending it here — as a shared `finally` used to — would strand the
      // customer on a transient 5xx: `hasRun` is already set, so not even a
      // reload could get them back. Keep them on the agreement and let them
      // press Continue again instead.
      Sentry.captureException(e)
      setHasAcceptFailed(true)
      setIsSubmitting(false)
      return
    }

    try {
      const outcome = await enrollInFreeTrialRequest({ campaignId, customerId })

      if (!isEnrolled(outcome)) {
        // Most likely the SUPER-only endpoint refusing a customer token. Stay
        // silent rather than welcoming someone to a trial they never got.
        Sentry.captureMessage(`Free trial enrolment did not apply: ${outcome}`, 'warning')
        setStage('idle')
        return
      }

      // The freshly granted allowance only exists server-side, so the profile
      // has to be refreshed before the modal can name the number.
      const user = await getUserFromIdRequest(userId)
      const current = getUserProfile()

      if (current) {
        storeUserProfile({ ...current, ...user, customerId: user.customer.id })
      }

      const trial = user.customer?.freeTrial

      // No trial came back — the write may not have propagated yet. Same
      // reasoning as a refused enrolment: say nothing rather than welcome them
      // to a trial of zero verifications. Null verifications is Head-to-Head
      // unlimited, which is a real allowance.
      if (!trial || trial.verifications === 0 || trial.verifications === undefined) {
        Sentry.captureMessage('Free trial enrolment returned no verifications', 'warning')
        setStage('idle')
        return
      }

      setVerifications(trial.verifications)
      setTrialDays(trial.campaign?.trialDays ?? null)
      setStage('welcome')
    } catch (e) {
      Sentry.captureException(e)
      setStage('idle')
    } finally {
      setIsSubmitting(false)
      // Only now that an enrolment was actually attempted. The acceptance is
      // recorded server-side at this point, and a retry of a refused enrolment
      // would only fail the same way.
      destroyFreeTrialId()
    }
  }

  if (stage === 'primer') {
    return <FreeTrialTermsPrimerModal onContinue={continueFromPrimer} />
  }

  if (stage === 'terms' && identity.current) {
    return (
      <FreeTrialTermsModal
        customerId={identity.current.customerId}
        campaignId={campaignId}
        isSubmitting={isSubmitting}
        hasAcceptFailed={hasAcceptFailed}
        onBack={() => setStage('primer')}
        onAgree={acceptTermsAndEnroll}
      />
    )
  }

  if (stage !== 'welcome' || verifications === undefined) {
    return null
  }

  return (
    <FreeTrialModal
      verifications={verifications}
      trialDays={trialDays}
      isCreating={isCreating}
      onCreateWorkflow={createWorkflow}
      onMaybeLater={() => window.location.reload()}
    />
  )
}

export default FreeTrialEnrollment
